import React, { useState, useEffect } from 'react';
import { Globe, Heart, Users } from 'lucide-react';
import { useSwipeable } from 'react-swipeable';
import './BannerCarousel.css';

interface BannerSlide {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  backgroundGradient: string;
}

const BannerCarousel: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const slides: BannerSlide[] = [
    {
      id: 1,
      title: "Find Love Across Continents",
      subtitle: "Your Perfect Match Awaits Globally",
      description: "Connect with compatible singles from around the world. DateNow's global community brings together hearts from every continent, helping you find meaningful relationships that transcend borders.",
      icon: <Globe size={60} />,
      backgroundGradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    },
    {
      id: 2,
      title: "Smart Compatibility Matching",
      subtitle: "Science Meets Romance",
      description: "Our matching system analyzes personality, values, and lifestyle to find your ideal partner. Experience precision matchmaking based on compatibility.",
      icon: <Heart size={60} />,
      backgroundGradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
    },
    {
      id: 3,
      title: "Verified Community Platform",
      subtitle: "Authentic Connections Only",
      description: "Join a trusted community of verified professionals and serious relationship seekers. Our secure platform ensures genuine connections with background-checked members committed to finding lasting love.",
      icon: <Users size={60} />,
      backgroundGradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, slides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => goToSlide((currentSlide + 1) % slides.length),
    onSwipedRight: () => goToSlide((currentSlide - 1 + slides.length) % slides.length),
    trackMouse: true
  });

  return (
    <div className="datenow-banner-carousel">
      <div className="datenow-carousel-container" {...handlers}>
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`datenow-carousel-slide ${index === currentSlide ? 'active' : ''}`}
            style={{ background: slide.backgroundGradient }}
          >
            <div className="datenow-slide-content">
              <div className="datenow-slide-text">
                <div className="datenow-slide-icon">{slide.icon}</div>
                <h1 className="datenow-slide-title">{slide.title}</h1>
                <h2 className="datenow-slide-subtitle">{slide.subtitle}</h2>
                <p className="datenow-slide-description">{slide.description}</p>
              </div>

              <div className="datenow-slide-visual">
                <div className="datenow-world-map">
                  <div className="datenow-profile-dots">
                    <div className="datenow-profile-dot datenow-dot-1">
                      <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face" alt="Profile" />
                      <span className="datenow-flag-icon">🇺🇸</span>
                    </div>
                    <div className="datenow-profile-dot datenow-dot-2">
                      <img src="https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face" alt="Profile" />
                      <span className="datenow-flag-icon">🇬🇧</span>
                    </div>
                    <div className="datenow-profile-dot datenow-dot-3">
                      <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face" alt="Profile" />
                      <span className="datenow-flag-icon">🇨🇦</span>
                    </div>
                    <div className="datenow-profile-dot datenow-dot-4">
                      <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face" alt="Profile" />
                      <span className="datenow-flag-icon">🇮🇳</span>
                    </div>
                    <div className="datenow-profile-dot datenow-dot-5">
                      <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face" alt="Profile" />
                      <span className="datenow-flag-icon">🇦🇺</span>
                    </div>
                    <div className="datenow-profile-dot datenow-dot-6">
                      <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face" alt="Profile" />
                      <span className="datenow-flag-icon">🇵🇰</span>
                    </div>
                  </div>

                  <div className="datenow-connection-lines">
                    <svg className="datenow-connection-svg" viewBox="0 0 400 300">
                      <path d="M50,150 Q200,50 350,150" stroke="rgba(255,255,255,0.3)" strokeWidth="2" fill="none" strokeDasharray="5,5">
                        <animate attributeName="stroke-dashoffset" values="0;-10" dur="2s" repeatCount="indefinite" />
                      </path>
                      <path d="M100,100 Q200,200 300,100" stroke="rgba(255,255,255,0.3)" strokeWidth="2" fill="none" strokeDasharray="5,5">
                        <animate attributeName="stroke-dashoffset" values="0;-10" dur="2.5s" repeatCount="indefinite" />
                      </path>
                      <path d="M80,200 Q200,100 320,200" stroke="rgba(255,255,255,0.3)" strokeWidth="2" fill="none" strokeDasharray="5,5">
                        <animate attributeName="stroke-dashoffset" values="0;-10" dur="3s" repeatCount="indefinite" />
                      </path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Dots Indicator */}
        <div className="datenow-carousel-dots">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`datenow-dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default BannerCarousel;
